import React, { useRef, useState } from "react";
import Popover from "./Popover";
import Months, { CalendarIcon } from "./Months";
import { allowed, clamp, dayLabel, monthStart, quickDay, todayYmd } from "./calendar.mjs";
import "./datepicker.css";

/*
 * The CRM's one-day picker, in place of <input type="date">. Same value
 * ("YYYY-MM-DD", or "" for none), but onChange gets the string itself.
 *
 * The browser's own picker drew a different calendar in every browser, in the
 * US month-first order, with a blue that is nowhere else in the CRM. This one
 * reads "Wed 21 Oct", opens on the chosen day, and can offer one-click days
 * (`quick`: { label, days } / { label, months } / { label, weekday }).
 */
export default function DateField({
  value,
  onChange,
  min,
  max,
  disabled,
  id,
  className = "",
  placeholder = "Pick a date",
  quick,
  clearable = false,
  align = "left",
  "aria-label": ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const btn = useRef(null);
  const today = todayYmd();

  const toggle = () => {
    if (!open) setView(monthStart(clamp(value || today, min, max)));
    setOpen(!open);
  };
  const pick = (day) => {
    onChange(day);
    setOpen(false);
    if (btn.current) btn.current.focus();
  };
  const quickDays = (quick || []).map((q) => ({ ...q, day: quickDay(q, today) }));

  return (
    <span className={`dp-field${disabled ? " is-disabled" : ""}`}>
      <button
        ref={btn}
        id={id}
        type="button"
        className={`dp-trigger ${className}${value ? "" : " is-empty"}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel ? `${ariaLabel}: ${value ? dayLabel(value) : "not set"}` : undefined}
        disabled={disabled}
        onClick={toggle}
      >
        <CalendarIcon size={14} />
        <span className="dp-trigger__text">{value ? dayLabel(value) : placeholder}</span>
      </button>
      {clearable && value && !disabled && (
        <button type="button" className="dp-clear" aria-label={`Clear ${ariaLabel || "date"}`} onClick={() => onChange("")}>
          ×
        </button>
      )}
      {open && (
        <Popover anchor={btn} onClose={() => setOpen(false)} align={align} label={ariaLabel || "Pick a date"} className="dp-pop--single">
          {quickDays.length > 0 && (
            <div className="dp-quick">
              {quickDays.map((q) => (
                <button
                  key={q.label}
                  type="button"
                  className={`dp-chip${q.day === value ? " is-on" : ""}`}
                  disabled={!allowed(q.day, min, max)}
                  title={dayLabel(q.day)}
                  onClick={() => pick(q.day)}
                >
                  {q.label}
                </button>
              ))}
            </div>
          )}
          <Months view={view} onView={setView} value={value} min={min} max={max} onPick={pick} />
          <div className="dp-foot">
            <button type="button" className="dp-link" disabled={!allowed(today, min, max)} onClick={() => pick(today)}>
              Today
            </button>
            {clearable && value && (
              <button type="button" className="dp-link" onClick={() => pick("")}>
                Clear
              </button>
            )}
          </div>
        </Popover>
      )}
    </span>
  );
}
